"use client"

import {Button} from "@/components/ui/button"
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from "@/components/ui/dialog"

import {Clock, Loader2, X} from "lucide-react"
import {AuthFormEnum} from "@/constants/auth-enum";
import React, {useState} from "react";
import {useForm} from "react-hook-form";
import {Label} from "@/components/ui/label";
import MediumButton from "@/components/global/buttons/medium-button";
import AuthenticationQueryHooks from "@/http/api/auth/AuthenticationQueryHooks";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {StaticWordEnum} from "@/constants/general-enum";
import OtpInput from "@/components/ui/inputs/otp-input";
import useCountdown from "@/hook/useCountdown";
import {useAuthStore} from "@/store/auth/authStore";
import Cookies from "js-cookie";
import {usePathname, useRouter} from "next/navigation";
import {Input} from "@/components/ui/input";


const queryClient = new QueryClient()


const {useRequest, useVerify} = new AuthenticationQueryHooks()

export default function WizardFormAuth({customButton}: { customButton: React.ReactNode }) {

    const [open, setOpen] = useState(false)
    const [step, setStep] = useState(1)

    const handleClose = () => {
        setOpen(false)
        setStep(1)
    }
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {customButton ? customButton : <Button>{AuthFormEnum.register}</Button>}
            </DialogTrigger>
            <DialogContent className="sm:max-w-md !rounded-2xl" dir="ltr">
                <DialogHeader className="flex border-b flex-row relative items-center justify-between">
                    <DialogTitle
                        className={"bg-white text-sm border-0 z-10 text-end w-11/12 px-0 gap-1 flex items-center flex-row-reverse"}>
                        <span className={"ps-0 text-sm md:text-base"}>
                            {AuthFormEnum.login}
                        </span>
                    </DialogTitle>
                    <Button variant="none" size="icon" className="rounded-full absolute right-0" onClick={handleClose}>
                        <X className="h-6 w-6"/>
                    </Button>
                </DialogHeader>

                <div dir={"rtl"} className={"w-full flex flex-wrap items-center justify-center px-5"}>
                    <div className="relative mb-6 w-full">
                        <div className={"w-full flex flex-wrap items-center justify-center space-y-2"}>
                            <QueryClientProvider client={queryClient}>
                                {step === 1 && <EnterContactFormComponent setStep={setStep}/>}
                                {step === 2 && <EnterOTPFormComponent setStep={setStep}/>}
                            </QueryClientProvider>
                        </div>
                    </div>
                </div>


            </DialogContent>
        </Dialog>
    )
}


const EnterContactFormComponent = ({setStep}: { setStep: (state: number) => void }) => {

    const {contact, setContact, setTimeLimit} = useAuthStore((state) => state);

    const {mutate, isPending} = useRequest()

    const {register, handleSubmit} = useForm({
        defaultValues: {
            contact: contact
        }
    })

    const onSubmit = (values: any) => {
        if (values) {
            setContact(values?.contact)
            mutate(values.contact, {
                onSuccess: onSuccessHandler
            })
        }
    }

    const onSuccessHandler = (response: any) => {
        if (response?.status) {
            setStep(2)
            toast.success(response.messages[0] ?? response.message, {position: "top-center"})
            setTimeLimit(response?.data?.expired_at_after_minute);
        }
    }


    return (
        <form onSubmit={handleSubmit(onSubmit)}
              className={"w-full flex flex-wrap content-between items-center space-y-5"}
        >
            <Label className={"w-full flex mt-4 md:mt-0 text-xs md:text-base  flex-wrap items-center"}>
                شماره همراه خود را وارد کنید
                <span className={"text-red-500"}>*</span>
            </Label>


            <div className={"w-full flex flex-wrap"}>
                <Input
                    {...register("contact", {
                        required: true,
                        pattern: {
                            value: /^[0-9]*$/,
                            message: "فرمت شماره تلفن درست نیست"
                        }
                    })}
                    autoFocus={true}
                    className={`text-center md:h-[64px] rounded-2xl  placeholder:text-right placeholder:font-medium !h-[48px] placeholder:text-xs md:placeholder:text-base`}
                    placeholder={StaticWordEnum.EnterPhone}
                    type="tel"
                    inputMode="numeric"
                    onKeyDown={(e) => {
                        // Prevent non-numeric characters
                        if (!/[0-9]/.test(e.key) &&
                            e.key !== 'Backspace' &&
                            e.key !== 'Tab' &&
                            e.key !== 'Enter' &&
                            e.key !== 'ArrowLeft' &&
                            e.key !== 'ArrowRight') {
                            e.preventDefault();
                        }
                    }}
                    onChange={(e) => {
                        e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    }}
                />
            </div>


            <div className={"w-full flex-wrap  flex"}>
                {
                    isPending ? (<Button disabled className={"w-full flex p-2"}>
                            <Loader2 className={"animate-spin"}/>
                        </Button>) :
                        <MediumButton variant={"default"} title={"تایید"} type={"submit"}
                                      customClass={"w-full h-[48px]"}/>
                }
            </div>

        </form>
    )
}

const EnterOTPFormComponent = ({setStep}: { setStep: (state: number) => void }) => {
    const router = useRouter()
    const {mutate, isPending} = useVerify()
    const {register, handleSubmit, setValue} = useForm()
    const {contact, timeLimit, setToken, setUser} = useAuthStore((state) => state);

    const verificationCountdown = useCountdown(timeLimit || 0);

    const onSubmit = (values: any) => {
        if (values) {
            mutate({
                otp: values?.otp,
                contact: contact,
            }, {
                onSuccess: onSuccessHandler,
            })
        }
    }

    const onSuccessHandler = (response: any) => {
        if (response?.statusCode === 200 || response?.status) {
            setTokenHandler(response)
            setToken(response?.data?.token);
            setUser(response?.data?.user);
            toast.success(response.messages[0], {position: "top-center"});
            setTimeout(() => {
                window.location.reload()
            }, 1000)
        } else {
            onErrorHandler(response)
        }
    }

    const onErrorHandler = (response: any) => {
        toast.error(response.messages ?? response.message, {position: "top-center"})
    }

    const setTokenHandler = (response: any) => {
        const token = response?.data?.token;
        if (token) {
            setToken(token)
            Cookies.set("token", token, {
                expires: 2,
                secure: true,
                sameSite: "strict",
                path: "/"
            });
            setTimeout(() => {
                window.location.reload();
            }, 1000)
        }
    }

    const changeNumberHandler = () => {
        setStep(1)
    }

    const handleOtpComplete = (otp: string) => {
        setValue("otp", otp);
        onSubmit({otp});
    };
    return (
        <form onSubmit={handleSubmit(onSubmit)}
              className={"w-full flex flex-wrap content-between items-center space-y-5"}
        >
            <Label className={"w-full mt-4 flex flex-wrap items-center"}>
                کد ارسال شده به شماره زیر را وارد کنید.
            </Label>

            <div className={"w-full flex flex-wrap items-center justify-between"}>
                <div className={"w-1/2 flex items-center justify-start text-sm"}>
                    {contact}
                </div>
                <div className={"w-1/2 flex items-center justify-end"}>
                    <Button type={"button"} onClick={changeNumberHandler} variant={"none"}
                            className={"text-orange-primary p-0 text-sm"}>
                        {StaticWordEnum.changeNumber}
                    </Button>
                </div>
            </div>


            <div className={"w-full flex flex-wrap"}>
                <OtpInput customClass={"!h-[48px] placeholder:text-xs md:placeholder:text-base"}
                          onComplete={handleOtpComplete} register={register}/>
            </div>


            <div className={"w-full flex-wrap flex"}>
                {
                    isPending ? (<Button disabled className={"w-full flex p-2"}>
                            <Loader2 className={"animate-spin"}/>
                        </Button>) :
                        <MediumButton variant={"default"} title={"تایید"} type={"submit"}
                                      customClass={"w-full h-[48px]"}/>
                }
            </div>

            <div className="w-full flex flex-wrap items-center justify-between gap-10">
                <Button
                    variant="none"
                    type={"button"}
                    disabled={verificationCountdown !== "00:00"}
                    onClick={() => setStep(1)}
                    className="text-blue-900 border !border-blue-secondary disabled:text-gray-600 rounded-xl h-9 w-1/2 md:w-48"
                >
                    {StaticWordEnum.resendTheVerificationCode}
                </Button>

                <Button type={"button"} variant="none" disabled={false}
                        className="text-blue-900 border !border-blue-secondary flex items-center justify-center gap-2 rounded-xl w-1/3 md:w-32 h-9">
                    <Clock/>
                    {verificationCountdown}
                </Button>
            </div>

        </form>
    )
}
